import React, { useEffect, useRef, useState } from 'react';

import { MtxButton, MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { Trans, useTranslation } from 'react-i18next';

import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import FixedActionsBar from '@/components/FixedActionsBar/FixedActionsBar';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import { Spinner } from '@/components/Spinner/Spinner';
import ContentModalQpsContact from '@/modals/ContentModalQpsContact/ContentModalQpsContact';
import trackQps from '@/utils/functions/tracking/trackQps/trackQps';

import styles from './styles.module.css';

import type { BreadcrumbItem } from '@/services/types/Breadcrumb';

interface QpsResultProps {
  isPassed: boolean;
  productName: string;
  nextProductName?: string;
  // Rang du questionnaire suivant : pas forcément le rang actuel + 1, un questionnaire déjà réussi
  // entre les deux est sauté.
  nextQuestionnaireIndex?: number;
  questionnaireIndex?: number;
  questionnaireCount?: number;
  // Tracking : questionnaire concerné et numéro de la tentative qui vient de se terminer (à partir de 1).
  codeSupport: string;
  attempt: number;
  breadcrumbItems: BreadcrumbItem[];
  // Chargement de l'acceptation déclenchée par « Continuer » sur le dernier questionnaire réussi
  // (cf. onAllPassed) : sans objet sur les autres variantes, qui ne mènent pas à cette action.
  isContinueLoading?: boolean;
  onContinue: () => void;
  onRetry: () => void;
  onBackToProposal: () => void;
}

type QpsResultVariant = 'success' | 'successNext' | 'failure';

// Ce qui change d'une issue à l'autre : icône, textes et action principale.
const VARIANTS = {
  success: {
    icon: 'check-circle-solid',
    isPassed: true,
    titleKey: 'qps.result.successTitle',
    messageKey: 'qps.result.successMessage',
    primaryKey: 'qps.result.continue',
  },
  successNext: {
    icon: 'check-circle-solid',
    isPassed: true,
    titleKey: 'qps.result.successMultiTitle',
    messageKey: 'qps.result.successMultiMessage',
    primaryKey: 'qps.result.nextQuestionnaire',
  },
  failure: {
    icon: 'cross-circle-solid',
    isPassed: false,
    titleKey: 'qps.result.failTitle',
    messageKey: 'qps.result.failMessage',
    primaryKey: 'qps.result.retry',
  },
} as const;

const QpsResult: React.FC<QpsResultProps> = ({
  isPassed,
  productName,
  nextProductName,
  nextQuestionnaireIndex,
  questionnaireIndex,
  questionnaireCount,
  codeSupport,
  attempt,
  breadcrumbItems,
  isContinueLoading = false,
  onContinue,
  onRetry,
  onBackToProposal,
}) => {
  const { t } = useTranslation();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  // La bascule questionnaire → résultat ne change pas de route : sans ce recentrage, rien n'est
  // annoncé et le focus reste sur le bouton démonté.
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Un questionnaire réussi avec un produit suivant enchaîne sur le questionnaire suivant.
  let variant: QpsResultVariant = 'failure';
  if (isPassed) variant = nextProductName === undefined ? 'success' : 'successNext';
  const config = VARIANTS[variant];
  const isMultiQuestionnaire = questionnaireCount !== undefined && questionnaireCount > 1;
  const titleKey = variant === 'failure' && isMultiQuestionnaire ? 'qps.result.failMultiTitle' : config.titleKey;
  // Seule la variante finale mène à onAllPassed (acceptation) : « Passer au questionnaire suivant »
  // reste une transition locale et synchrone, jamais concernée par ce chargement.
  const isContinueBusy = variant === 'success' && isContinueLoading;

  return (
    <div className={styles.screen}>
      <Breadcrumb items={breadcrumbItems} alternative onBack={onBackToProposal} />

      <div className={styles.column}>
        <div className={styles.status}>
          <span aria-hidden="true" className={config.isPassed ? styles.iconPassed : styles.iconFailed}>
            <MtxIcon name={config.icon} size={64} />
          </span>
          <h1 className={styles.title} ref={titleRef} tabIndex={-1}>
            {t(titleKey, { index: questionnaireIndex, total: questionnaireCount })}
          </h1>
        </div>

        <p className={styles.message}>
          <Trans
            i18nKey={config.messageKey}
            values={{ product: productName, nextProduct: nextProductName }}
            components={{ b: <strong className={styles.productName} /> }}
          />
        </p>

        {variant === 'failure' && (
          <div className={styles.contact}>
            <p className={styles.contactQuestion}>{t('qps.result.contactQuestion')}</p>
            <button
              type="button"
              className={styles.contactLink}
              onClick={() => {
                trackQps('nous_contacter', { codeSupport, attempt });
                setIsContactOpen(true);
              }}
            >
              {t('qps.result.contactLink')}
            </button>
          </div>
        )}
      </div>

      <FixedActionsBar centered stackOnMobile>
        {variant !== 'success' && (
          <MtxButton
            className={styles.actionButton}
            variant="tertiary"
            size="large"
            fullWidth
            onClick={() => {
              trackQps('retour_proposition', { codeSupport, attempt });
              onBackToProposal();
            }}
          >
            <span>{t('qps.result.backToProposal')}</span>
          </MtxButton>
        )}

        <MtxButton
          className={styles.actionButton}
          variant="primary"
          size="large"
          fullWidth
          onClick={() => {
            if (variant === 'failure') {
              onRetry();
            } else if (!isContinueBusy) {
              onContinue();
            }
          }}
          aria-busy={isContinueBusy}
          aria-disabled={isContinueBusy}
        >
          <div className={styles.buttonContent}>
            {isContinueBusy ? (
              <Spinner color="--totem-color-white" diameter={24} />
            ) : (
              <span>{t(config.primaryKey, { index: nextQuestionnaireIndex })}</span>
            )}
          </div>
        </MtxButton>
      </FixedActionsBar>

      {isContactOpen && (
        <ModaleDefaut
          title=""
          size="medium"
          mobileStyle="stick-to-bottom"
          content={
            <ContentModalQpsContact
              onRequestClose={() => setIsContactOpen(false)}
              onChannelSelect={(channel) => trackQps(channel, { codeSupport, attempt })}
            />
          }
          onClickClose={() => setIsContactOpen(false)}
        />
      )}
    </div>
  );
};

export default QpsResult;
